import type { OnboardingState } from './onboarding-state.service';

/**
 * LA VERSION DE L'ETAT D'INSCRIPTION PERSISTE DANS LE NAVIGATEUR.
 *
 * L'etat vit dans `localStorage` (`sf_onboarding_state`) et survit a la
 * deconnexion comme a la fermeture du navigateur. Il peut donc avoir ete ecrit
 * par une version anterieure de l'application.
 *
 * ── POURQUOI IL FALLAIT LE VERSIONNER ──
 *
 * La decision D2 change le sens de `CA` : c'etait « Canada (Quebec) », c'est le
 * Canada (pays), le Quebec devenant `CA-QC`. Un etat ecrit avant la bascule qui
 * porte `jurisdictionCode: 'CA'` ne dit plus ce que l'utilisateur avait choisi.
 * Le renvoyer tel quel au serveur creerait une entreprise canadienne SANS
 * province, donc sans NEQ ni TVQ, sans que rien ne le signale. Les etapes 3 et
 * 4 ont aussi change de forme (champs d'adresse et identifiants fiscaux pilotes
 * par la juridiction).
 *
 * ── LA REGLE ──
 *
 * - version absente ou differente de la courante : l'etat est PURGE, pas
 *   « migre ». Deviner le Quebec derriere un `CA` ancien serait inventer ;
 *   l'utilisateur refait l'assistant, qui est court.
 * - JSON illisible ou forme inattendue : purge aussi. Jamais d'exception.
 * - Un etat de la version courante est complete par les valeurs par defaut :
 *   un champ manquant ne fait jamais planter un ecran.
 */
export const ONBOARDING_STATE_VERSION = 2;

/** Ce que donne la lecture d'une valeur brute du stockage. */
export type LectureEtat =
    | { type: 'absent' }
    | { type: 'purge'; raison: 'illisible' | 'forme' | 'version' }
    | { type: 'valide'; etat: OnboardingState };

const estObjet = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

/** Lit une valeur brute du stockage ; ne leve jamais. */
export function lireEtatPersiste(brut: string | null, parDefaut: OnboardingState): LectureEtat {
    if (brut === null || brut === '') return { type: 'absent' };

    let json: unknown;
    try {
        json = JSON.parse(brut);
    } catch {
        return { type: 'purge', raison: 'illisible' };
    }
    if (!estObjet(json)) return { type: 'purge', raison: 'forme' };
    if (json['version'] !== ONBOARDING_STATE_VERSION) return { type: 'purge', raison: 'version' };

    const { currentStep, jurisdictionCode, jurisdictionMeta, activitySectorId, stepsCompleted } = json;
    const sectionsOk = (['identity', 'contactLocation', 'fiscalConfig', 'subscription', 'adminConfig'] as const).every(
        (k) => estObjet(json[k]),
    );
    const formeOk =
        typeof currentStep === 'number' &&
        currentStep >= 1 &&
        currentStep <= 6 &&
        (jurisdictionCode === null || typeof jurisdictionCode === 'string') &&
        estObjet(jurisdictionMeta) &&
        (activitySectorId === null || typeof activitySectorId === 'number') &&
        estObjet(stepsCompleted) &&
        sectionsOk;
    if (!formeOk) return { type: 'purge', raison: 'forme' };

    return { type: 'valide', etat: { ...parDefaut, ...(json as unknown as OnboardingState) } };
}

/**
 * Le code de juridiction change : ce qui en dependait ne vaut plus.
 *
 * - le contenu de l'etape 3 propre a la juridiction (champs d'adresse) ;
 * - les identifiants fiscaux de l'etape 4 : leurs codes (`fi_CA-NEQ`...) sont
 *   concatenes par le serveur dans `fiscal_id` ; ceux d'une autre juridiction
 *   finiraient sur l'entreprise ;
 * - la forme juridique et la devise de l'etape 2.
 *
 * Les etapes 2, 3 et 4 redeviennent « a faire » ; le reste (nom, courriel,
 * abonnement, secteur) est independant de la juridiction et se conserve.
 */
export function etatApresChangementDeJuridiction(etat: OnboardingState): OnboardingState {
    const identite = { ...etat.identity };
    delete identite['legalForm'];
    delete identite['currencyId'];
    const { email, phone, website } = etat.contactLocation;
    const coordonnees: Record<string, unknown> = {};
    if (email !== undefined) coordonnees['email'] = email;
    if (phone !== undefined) coordonnees['phone'] = phone;
    if (website !== undefined) coordonnees['website'] = website;

    const fait: Record<number, boolean> = { 1: true };
    if (etat.stepsCompleted[5]) fait[5] = true;
    return {
        ...etat,
        identity: identite,
        contactLocation: coordonnees,
        fiscalConfig: {},
        stepsCompleted: fait,
    };
}
